/**
 * Coach Engine — Priority & Ranking
 *
 * Given a list of observations from multiple analyzers, rank them so the
 * most important signals surface first. UI surfaces typically want the
 * top 2–3 observations for brevity.
 */

import { SEVERITY } from './types';

// Severity weight — warnings/criticals outrank info/success by default.
const SEVERITY_WEIGHT = Object.freeze({
  [SEVERITY.CRITICAL]: 40,
  [SEVERITY.WARNING]:  30,
  [SEVERITY.SUCCESS]:  15,
  [SEVERITY.INFO]:     10
});

/**
 * Compute a ranking score for an observation. Higher = more important.
 *
 * score = analyzer-supplied priority
 *       + severity weight
 *       + confidence bonus (up to +15)
 *
 * Actionable observations (have a non-empty `action` field) get a small bump.
 *
 * @param {Observation} obs
 * @returns {number}
 */
export const scoreObservation = (obs) => {
  if (!obs) return 0;
  const severity = SEVERITY_WEIGHT[obs.severity] ?? 0;
  const confidence = Math.round((obs.confidence ?? 0.7) * 15);
  const actionable = obs.action ? 5 : 0;
  return (obs.priority ?? 50) + severity + confidence + actionable;
};

/**
 * Rank observations by score, descending. Returns a new array.
 *
 * @param {Observation[]} observations
 * @returns {Observation[]}
 */
export const rankObservations = (observations) => {
  if (!Array.isArray(observations)) return [];
  return [...observations]
    .filter(Boolean)
    .sort((a, b) => scoreObservation(b) - scoreObservation(a));
};

/**
 * Dedupe observations that describe the same underlying signal. Two
 * observations are considered duplicates if they share `analyzer + scopeId`,
 * OR they share a stable `id`. When duplicates are found, keep the higher-scored one.
 *
 * @param {Observation[]} observations
 * @returns {Observation[]}
 */
export const dedupeObservations = (observations) => {
  if (!Array.isArray(observations)) return [];
  const byKey = new Map();
  for (const obs of observations) {
    if (!obs) continue;
    // Prefer the explicit `id` (which every shipped analyzer sets). The
    // fallback used to be `${analyzer}:${scopeId}` which collapsed multiple
    // legitimate observations from the same analyzer-on-the-same-scope (e.g.
    // dayBaselineCompare emits up to two observations per day) — works today
    // by convention since all analyzers DO set an id, but a future analyzer
    // that forgets would silently lose observations. Falling back to a
    // title-bearing key keeps each unique observation distinct.
    const key = obs.id || `${obs.analyzer || 'x'}:${obs.scopeId || ''}:${obs.title || ''}`;
    const existing = byKey.get(key);
    if (!existing || scoreObservation(obs) > scoreObservation(existing)) {
      byKey.set(key, obs);
    }
  }
  return Array.from(byKey.values());
};

/**
 * Take the top N observations, ranked and deduped. The most common
 * use case for UI surfaces.
 *
 * @param {Observation[]} observations
 * @param {number} n
 * @returns {Observation[]}
 */
export const topObservations = (observations, n = 3) => {
  return rankObservations(dedupeObservations(observations)).slice(0, Math.max(0, n));
};
